// Auto Contrast — stretches the luminance histogram to cover the full range,
// applying the same transform to all channels equally. This preserves the
// color balance while improving contrast (unlike Auto Tone which can shift
// colors by stretching each channel independently).

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface AutoContrastParams {
  /** Fraction of pixels to clip from each end (default 0.001 = 0.1%) */
  clip: number;
}

export const AutoContrast: Filter<AutoContrastParams> = {
  id: "auto-contrast",
  displayName: "Auto Contrast",
  defaultParams: { clip: 0.001 },

  apply(input: PixelBuffer, params: AutoContrastParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    // Build a combined luminance histogram
    const hist = new Uint32Array(256);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o]!;
      const g = src[o + 1]!;
      const b = src[o + 2]!;
      const lum = (r * 77 + g * 150 + b * 29) >> 8;
      hist[lum] = (hist[lum] ?? 0) + 1;
    }

    const total = n;
    const clipFrac = clampRange(params.clip, 0, 0.5);
    const threshold = Math.round(total * clipFrac);
    const minVal = findPercentile(hist, threshold);
    const maxVal = findPercentile(hist, total - threshold);
    const lut = buildStretchLut(minVal, maxVal);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = lut[src[o]!]!;
      dst[o + 1] = lut[src[o + 1]!]!;
      dst[o + 2] = lut[src[o + 2]!]!;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};

function findPercentile(hist: Uint32Array, target: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) {
    sum += hist[i] ?? 0;
    if (sum >= target) return i;
  }
  return 255;
}

function buildStretchLut(minVal: number, maxVal: number): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  if (maxVal <= minVal) { lut.fill(0); return lut; }
  const scale = 255 / (maxVal - minVal);
  for (let v = 0; v < 256; v++) {
    if (v <= minVal) lut[v] = 0;
    else if (v >= maxVal) lut[v] = 255;
    else lut[v] = Math.round((v - minVal) * scale);
  }
  return lut;
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
